import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, MotionConfig, AnimatePresence } from 'framer-motion';
import { useLenis } from '@/hooks/useLenis';
import { FadeSection } from '@/components/shared/FadeSection';
import { SmartLink } from '@/components/shared/SmartLink';
import '@/styles/safety.css';

const ease = [0.22, 1, 0.36, 1];

const REPORT_REASONS = [
  'Inappropriate behaviour',
  'Harassment or pressure',
  'Fake or misleading profile',
  'Safety concern',
  'Other',
];

const SafetyPage = () => {
  useLenis();

  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const openModal = useCallback(() => {
    setSubmitted(false);
    setReason('');
    setNote('');
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);
  const handleSubmit = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (!reason) return;
      setSubmitted(true);
    },
    [reason],
  );

  return (
    <MotionConfig reducedMotion="user">
    <div className="safety-page" data-testid="safety-page">
      <div className="noise-overlay" aria-hidden="true" />

      {/* ── 1. HERO ─────────────────────────────────────────────── */}
      <section className="safety-hero" data-testid="safety-hero">
        <picture className="safety-hero-picture" aria-hidden="true">
          <img
            src="/assets/safety-top-hero.png"
            alt=""
            loading="eager"
            decoding="async"
            className="safety-hero-img"
            data-testid="safety-hero-image"
          />
        </picture>
        <div className="safety-hero-tint" aria-hidden="true" />
        <div className="safety-hero-fade" aria-hidden="true" />
        <div className="safety-hero-inner">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8, ease }}
            className="safety-eyebrow"
          >
            SAFETY
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.9, ease }}
            className="safety-hero-headline"
          >
            Safety, privacy, and intention &mdash; built in.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32, duration: 0.9, ease }}
            className="safety-hero-body"
          >
            NYMPHÉ is designed for connection with clearer boundaries, stronger
            trust signals, and a calmer way to move from interest to interaction.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.48, duration: 0.8, ease }}
            className="safety-hero-links"
          >
            <Link
              to="/"
              className="safety-quiet-link"
              data-testid="safety-return-home"
            >
              Return to Home
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── 2. SAFETY PRINCIPLES (3 cards) ──────────────────────── */}
      <section className="safety-principles" data-testid="safety-principles">
        <div className="safety-principles-inner">
          <FadeSection>
            <p className="safety-section-eyebrow">PRINCIPLES</p>
          </FadeSection>
          <FadeSection delay={0.08}>
            <h2 className="safety-section-head">The standards underneath it all.</h2>
          </FadeSection>

          <div className="safety-principles-grid">
            <FadeSection delay={0.16} className="safety-principle-card" data-testid="safety-principle-respect">
              <p className="safety-principle-num">I</p>
              <h3 className="safety-principle-title">Respect is the baseline.</h3>
              <p className="safety-principle-body">
                Harassment, impersonation, scams, coercion, and abusive behaviour are not tolerated.
              </p>
            </FadeSection>
            <FadeSection delay={0.22} className="safety-principle-card" data-testid="safety-principle-privacy">
              <p className="safety-principle-num">II</p>
              <h3 className="safety-principle-title">Privacy stays in your hands.</h3>
              <p className="safety-principle-body">
                Share what feels right, when it feels right. Location and personal details should always remain your choice.
              </p>
            </FadeSection>
            <FadeSection delay={0.28} className="safety-principle-card" data-testid="safety-principle-trust">
              <p className="safety-principle-num">III</p>
              <h3 className="safety-principle-title">Trust is earned.</h3>
              <p className="safety-principle-body">
                Verification, profile signals, and mutual confirmation tools help create a more accountable environment.
              </p>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* ── 3. VERIFIED INTERACTIONS ────────────────────────────── */}
      <section className="safety-block safety-block--split" data-testid="safety-verified">
        <div className="safety-block-inner safety-block-inner--split">
          <div className="safety-split-copy">
            <FadeSection>
              <p className="safety-section-eyebrow">VERIFIED INTERACTIONS</p>
            </FadeSection>
            <FadeSection delay={0.08}>
              <h2 className="safety-section-head">A layer of trust, beyond the profile.</h2>
            </FadeSection>
            <FadeSection delay={0.16}>
              <p className="safety-block-body">
                NYMPHÉ supports safer real-world connection through mutual
                confirmation. When two people choose to verify an interaction,
                only what both agree on can appear.
              </p>
            </FadeSection>
            <FadeSection delay={0.26}>
              <ul className="safety-feature-list safety-feature-list--single">
                <li>Two-way confirmation</li>
                <li>Community-backed trust</li>
                <li>Real-world validation</li>
                <li>No public detail without mutual agreement</li>
              </ul>
            </FadeSection>
            <FadeSection delay={0.34}>
              <Link
                to="/verified-encounters"
                className="safety-verified-more-info"
                aria-label="Learn more about Verified Encounters"
                data-testid="safety-verified-more-info"
              >
                More info
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" />
                  <path d="m13 6 6 6-6 6" />
                </svg>
              </Link>
            </FadeSection>
          </div>
          <FadeSection delay={0.18} className="safety-split-media">
            <picture className="safety-verified-ui-frame">
              <img
                src="/images/safety-verified-encounters-ui.png"
                alt="NYMPHÉ Verified Encounters screen on iPhone showing pending approvals, published profile notes, submissions, and privacy preferences."
                loading="lazy"
                decoding="async"
                className="safety-verified-ui-img"
                data-testid="safety-verified-ui-image"
              />
            </picture>
          </FadeSection>
        </div>
      </section>

      {/* ── 3.5 PRIVACY & CONTROL · full-bleed cinematic pause ──── */}
      <section
        className="safety-pc-beat"
        data-testid="safety-privacy-control-beat"
        aria-label="Privacy and control"
      >
        <picture className="safety-pc-beat-picture" aria-hidden="true">
          <source media="(max-width: 768px)" srcSet="/assets/safety-privacy-control-m.jpg" />
          <img
            src="/assets/safety-privacy-control.jpg"
            alt=""
            loading="lazy"
            decoding="async"
            className="safety-pc-beat-img"
            data-testid="safety-pc-beat-image"
          />
        </picture>
        <div className="safety-pc-beat-tint" aria-hidden="true" />
        <div className="safety-pc-beat-fade" aria-hidden="true" />
        <div className="safety-pc-beat-inner">
          <FadeSection>
            <p className="safety-pc-beat-eyebrow">PRIVACY &amp; CONTROL</p>
          </FadeSection>
          <FadeSection delay={0.08}>
            <h2 className="safety-pc-beat-head">
              You decide what&rsquo;s seen.
              <br />
              And what isn&rsquo;t.
            </h2>
          </FadeSection>
          <FadeSection delay={0.18}>
            <p className="safety-pc-beat-body">
              Not everything needs to be visible.
            </p>
          </FadeSection>
          <FadeSection delay={0.26}>
            <p className="safety-pc-beat-body">
              Some moments are private.
              <br />
              Some connections stay between two people.
            </p>
          </FadeSection>
          <FadeSection delay={0.36}>
            <p className="safety-pc-beat-body safety-pc-beat-body--key">
              On NYMPHÉ, control isn&rsquo;t a setting &mdash;
              <br />
              it&rsquo;s the foundation.
            </p>
          </FadeSection>
          <FadeSection delay={0.48}>
            <p className="safety-pc-beat-link-wrap">
              <a
                href="#safety-privacy"
                className="safety-quiet-link"
                data-testid="safety-pc-beat-link"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector('[data-testid="safety-privacy"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                Learn how privacy works
              </a>
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ── 4. PRIVACY + DISCRETION ─────────────────────────────── */}
      <section className="safety-block safety-block--alt" data-testid="safety-privacy">
        <div className="safety-block-inner">
          <FadeSection>
            <p className="safety-section-eyebrow">PRIVACY &amp; DISCRETION</p>
          </FadeSection>
          <FadeSection delay={0.08}>
            <h2 className="safety-section-head">Designed for discretion.</h2>
          </FadeSection>
          <FadeSection delay={0.16}>
            <p className="safety-block-body">
              NYMPHÉ is built for people who value privacy. The experience
              should feel calm, controlled, and intentional &mdash; never
              exposed, noisy, or careless.
            </p>
          </FadeSection>
          <FadeSection delay={0.26}>
            <ul className="safety-feature-list">
              <li>Screenshot-aware privacy</li>
              <li>Controlled profile visibility</li>
              <li>Optional location sharing</li>
              <li>Clear account boundaries</li>
            </ul>
          </FadeSection>
        </div>
      </section>

      {/* ── 4.5 REPORT WITHIN NYMPHÉ · private micro-action ─────── */}
      <section className="safety-report-in" data-testid="safety-report-in">
        <div className="safety-report-in-inner">
          <FadeSection>
            <p className="safety-section-eyebrow">REPORT WITHIN NYMPHÉ</p>
          </FadeSection>
          <FadeSection delay={0.08}>
            <h2 className="safety-section-head">Report within NYMPHÉ</h2>
          </FadeSection>
          <FadeSection delay={0.16}>
            <p className="safety-block-body">
              If something doesn&rsquo;t feel right, you can act immediately
              &mdash; quietly and on your terms.
            </p>
          </FadeSection>
          <FadeSection delay={0.26}>
            <div className="safety-report-in-actions">
              <Link
                to="/report?target=profile"
                className="safety-primary-cta"
                data-testid="safety-report-profile-btn"
              >
                Report a Profile
              </Link>
              <p className="safety-report-in-trust">
                All reports are private. We never notify the other person.
              </p>
              <div className="safety-report-in-secondary">
                <Link
                  to="/report?target=profile"
                  className="safety-quiet-link safety-report-in-link"
                  data-testid="safety-block-remove"
                >
                  Block &amp; Remove
                </Link>
                <span className="safety-report-in-sep" aria-hidden="true">·</span>
                <Link
                  to="/report?target=conversation"
                  className="safety-quiet-link safety-report-in-link"
                  data-testid="safety-report-message"
                >
                  Report a Message
                </Link>
                <span className="safety-report-in-sep" aria-hidden="true">·</span>
                <SmartLink
                  to="/privacy"
                  className="safety-quiet-link safety-report-in-link"
                  data-testid="safety-privacy-settings"
                >
                  Manage Privacy Settings
                </SmartLink>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* ── 5. REPORTING + MODERATION ───────────────────────────── */}
      <section className="safety-block" data-testid="safety-reporting">
        <div className="safety-block-inner">
          <FadeSection>
            <p className="safety-section-eyebrow">REPORTING &amp; MODERATION</p>
          </FadeSection>
          <FadeSection delay={0.08}>
            <h2 className="safety-section-head">If something feels off, it matters.</h2>
          </FadeSection>
          <FadeSection delay={0.16}>
            <p className="safety-block-body">
              Members should be able to report concerning behaviour,
              suspicious profiles, harassment, impersonation, scams, or
              boundary violations.
            </p>
          </FadeSection>
          <FadeSection delay={0.26}>
            <ul className="safety-feature-list">
              <li>Clear reporting pathway</li>
              <li>Review process</li>
              <li>Removal of unsafe accounts where required</li>
              <li>Zero tolerance for harassment, scams, and impersonation</li>
            </ul>
          </FadeSection>
          <FadeSection delay={0.36}>
            <p className="safety-report-link-wrap">
              <SmartLink
                to="/contact"
                className="safety-quiet-link"
                data-testid="safety-report-support-link"
              >
                Report to Support
              </SmartLink>
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ── 5.5 COMMON QUESTIONS · compact FAQ block ────────────── */}
      <section className="safety-faq" data-testid="safety-faq">
        <div className="safety-faq-inner">
          <FadeSection>
            <p className="safety-section-eyebrow">COMMON QUESTIONS</p>
          </FadeSection>

          <div className="safety-faq-list">
            <FadeSection delay={0.08} className="safety-faq-item">
              <h3 className="safety-faq-q" data-testid="safety-faq-item-1">Is my information private?</h3>
              <p className="safety-faq-a">
                Your privacy is always in your control. What you share, when
                you share it, and with who &mdash; is entirely up to you.
              </p>
            </FadeSection>

            <FadeSection delay={0.16} className="safety-faq-item">
              <h3 className="safety-faq-q" data-testid="safety-faq-item-2">How do I report someone?</h3>
              <p className="safety-faq-a">
                If something feels off, you can report it directly through the
                platform or contact support. Every report is reviewed
                carefully.
              </p>
            </FadeSection>

            <FadeSection delay={0.24} className="safety-faq-item">
              <h3 className="safety-faq-q" data-testid="safety-faq-item-3">What happens after I report?</h3>
              <p className="safety-faq-a">
                Reports are assessed by our moderation team. Where needed,
                action is taken to protect the integrity and safety of the
                platform.
              </p>
            </FadeSection>
          </div>

          <FadeSection delay={0.34}>
            <p className="safety-faq-link-wrap">
              <SmartLink
                to="/faq"
                className="safety-quiet-link"
                data-testid="safety-faq-view-full"
              >
                View full FAQ
              </SmartLink>
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ── 5.9 BOTTOM HERO · emotional anchor before the final CTA ── */}
      <section className="safety-bottom-hero" data-testid="safety-bottom-hero">
        <div className="safety-bottom-hero-media">
          <img
            src="/images/safety-bottom-hero.png"
            alt="A woman and her partner seated together on a plum velvet sofa, sharing a calm, intimate moment — representing the quiet trust NYMPHÉ is built around."
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="safety-bottom-hero-overlay" aria-hidden="true"></div>
        <div className="safety-bottom-hero-content">
          <h2>Safe connection starts with the right energy.</h2>
          <p>Built to protect, designed to feel right.</p>
        </div>
      </section>

      {/* ── 6. FINAL CTA ────────────────────────────────────────── */}
      <section className="safety-final" data-testid="safety-final">
        <div className="safety-final-inner">
          <FadeSection>
            <h2 className="safety-final-head">
              A safer way to connect starts with intention.
            </h2>
          </FadeSection>
          <FadeSection delay={0.14}>
            <SmartLink
              to="/access"
              className="safety-final-cta"
              data-testid="safety-final-cta"
            >
              Request Access
            </SmartLink>
          </FadeSection>
        </div>
      </section>

      {/* ── GET SUPPORT · external third-party resources (final section) ── */}
      <section className="safety-support" data-testid="safety-support">
        <div className="safety-support-inner">
          <FadeSection>
            <p className="safety-section-eyebrow">GET SUPPORT</p>
          </FadeSection>
          <FadeSection delay={0.08}>
            <h2 className="safety-section-head">Get Support</h2>
          </FadeSection>
          <FadeSection delay={0.16}>
            <p className="safety-block-body safety-support-lede">
              If something doesn&rsquo;t feel right, you&rsquo;re not alone.
              These services offer confidential support, guidance, and
              immediate help when needed.
            </p>
          </FadeSection>

          <div className="safety-support-list">
            <FadeSection delay={0.22} className="safety-support-card" data-testid="safety-support-1800respect">
              <p className="safety-support-card-name">1800RESPECT</p>
              <p className="safety-support-card-desc">
                National sexual assault, domestic and family violence support.
              </p>
              <div className="safety-support-card-links">
                <a
                  href="tel:+611800737732"
                  className="safety-support-card-link"
                  data-testid="safety-support-1800respect-phone"
                >
                  1800 737 732
                </a>
                <span className="safety-support-card-sep" aria-hidden="true">·</span>
                <a
                  href="https://www.1800respect.org.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="safety-support-card-link"
                  data-testid="safety-support-1800respect-web"
                >
                  1800respect.org.au
                </a>
              </div>
            </FadeSection>

            <FadeSection delay={0.28} className="safety-support-card" data-testid="safety-support-lifeline">
              <p className="safety-support-card-name">Lifeline Australia</p>
              <p className="safety-support-card-desc">
                24/7 crisis support and suicide prevention.
              </p>
              <div className="safety-support-card-links">
                <a
                  href="tel:+61131114"
                  className="safety-support-card-link"
                  data-testid="safety-support-lifeline-phone"
                >
                  13 11 14
                </a>
                <span className="safety-support-card-sep" aria-hidden="true">·</span>
                <a
                  href="https://www.lifeline.org.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="safety-support-card-link"
                  data-testid="safety-support-lifeline-web"
                >
                  lifeline.org.au
                </a>
              </div>
            </FadeSection>

            <FadeSection delay={0.34} className="safety-support-card" data-testid="safety-support-esafety">
              <p className="safety-support-card-name">eSafety Commissioner</p>
              <p className="safety-support-card-desc">
                Report online abuse, image-based abuse, or unsafe digital behaviour.
              </p>
              <div className="safety-support-card-links">
                <a
                  href="https://www.esafety.gov.au/report"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="safety-support-card-link"
                  data-testid="safety-support-esafety-web"
                >
                  esafety.gov.au/report
                </a>
              </div>
            </FadeSection>

            <FadeSection delay={0.40} className="safety-support-card" data-testid="safety-support-qlife">
              <p className="safety-support-card-name">QLife Australia</p>
              <p className="safety-support-card-desc">
                LGBTQIA+ peer support and referral service.
              </p>
              <div className="safety-support-card-links">
                <a
                  href="tel:+611800184527"
                  className="safety-support-card-link"
                  data-testid="safety-support-qlife-phone"
                >
                  1800 184 527
                </a>
                <span className="safety-support-card-meta">3pm &ndash; midnight</span>
                <span className="safety-support-card-sep" aria-hidden="true">·</span>
                <a
                  href="https://qlife.org.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="safety-support-card-link"
                  data-testid="safety-support-qlife-web"
                >
                  qlife.org.au
                </a>
              </div>
            </FadeSection>
          </div>

          <FadeSection delay={0.52}>
            <p className="safety-support-coda" data-testid="safety-support-coda">
              Your safety comes first &mdash; always.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ── Report modal (UI simulation only — no backend call) ── */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            key="safety-report-overlay"
            className="safety-modal-overlay"
            data-testid="safety-report-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="safety-report-modal-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease }}
            onClick={closeModal}
          >
            <motion.div
              className="safety-modal"
              initial={{ opacity: 0, scale: 0.965, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.965, y: 6 }}
              transition={{ duration: 0.22, ease }}
              onClick={(e) => e.stopPropagation()}
            >
              {!submitted ? (
                <form onSubmit={handleSubmit}>
                  <h3 id="safety-report-modal-title" className="safety-modal-title">
                    Report this profile
                  </h3>
                  <div className="safety-modal-options" role="radiogroup" aria-label="Reason">
                    {REPORT_REASONS.map((r) => {
                      const selected = reason === r;
                      return (
                        <label
                          key={r}
                          className={
                            'safety-modal-option' +
                            (selected ? ' safety-modal-option--selected' : '')
                          }
                          data-testid={`safety-report-reason-${r.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                        >
                          <input
                            type="radio"
                            name="safety-report-reason"
                            value={r}
                            checked={selected}
                            onChange={() => setReason(r)}
                            className="sr-only"
                          />
                          <span className="safety-modal-option-radio" aria-hidden="true" />
                          <span className="safety-modal-option-label">{r}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="safety-modal-note">
                    <label htmlFor="safety-report-note" className="safety-modal-note-label">
                      Add a note (optional)
                    </label>
                    <textarea
                      id="safety-report-note"
                      data-testid="safety-report-note"
                      className="safety-modal-note-input"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="safety-modal-actions">
                    <button
                      type="button"
                      className="safety-modal-cancel"
                      data-testid="safety-report-cancel"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="safety-modal-submit"
                      data-testid="safety-report-submit"
                      disabled={!reason}
                    >
                      Submit Report
                    </button>
                  </div>
                </form>
              ) : (
                <div className="safety-modal-confirm" data-testid="safety-report-confirm">
                  <div className="safety-modal-confirm-icon" aria-hidden="true" />
                  <h3 className="safety-modal-confirm-head">
                    Thank you. Your report has been received and reviewed discreetly.
                  </h3>
                  <p className="safety-modal-confirm-body">
                    You won&rsquo;t be visible to this profile again.
                  </p>
                  <button
                    type="button"
                    className="safety-modal-confirm-close"
                    data-testid="safety-report-confirm-close"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </MotionConfig>
  );
};

export default SafetyPage;
